import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const estImage = (f) => /\.(jpe?g|png|webp)$/i.test(f.name);

const COLONNES = {
  banque: [["date", "Date", "w-28"], ["description", "Description", "min-w-[16rem]"], ["debit", "Débit", "w-24"], ["credit", "Crédit", "w-24"]],
  plateforme: [["date", "Date", "w-28"], ["code", "Code", "w-32"], ["nom_client", "Client", "w-32"], ["date_debut", "Arrivée", "w-28"], ["date_fin", "Départ", "w-28"],
    ["nuits", "Nuits", "w-16"], ["loyer", "Nuitées", "w-24"], ["frais_menage", "Ménage", "w-20"], ["commission", "Commission", "w-24"],
    ["taxes_versees", "Taxes", "w-20"], ["total_verse", "Versé", "w-24"]],
};
const NUMS = ["debit", "credit", "nuits", "loyer", "frais_menage", "commission", "taxes_versees", "total_verse"];
const vide = (type) => Object.fromEntries(COLONNES[type === "banque" ? "banque" : "plateforme"].map(([k]) => [k, NUMS.includes(k) ? 0 : ""]));

const versEnvoi = (l) => Object.fromEntries(Object.entries(l).map(([k, v]) => [k, NUMS.includes(k) ? (v === "" || v === null ? (k === "total_verse" ? null : 0) : Number(String(v).replace(",", "."))) : v]));

const Grille = ({ type, lignes, setLignes }) => {
  const cols = COLONNES[type === "banque" ? "banque" : "plateforme"];
  const maj = (i, k, v) => setLignes(lignes.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="max-h-[55vh] overflow-auto rounded-md border border-slate-200">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
          <tr>{cols.map(([k, lib]) => <th key={k} className="px-2 py-1.5 font-medium">{lib}</th>)}<th /></tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`image-ligne-${i}`}>
              {cols.map(([k, , w]) => (
                <td key={k} className="px-1 py-1">
                  <Input value={l[k] ?? ""} onChange={(e) => maj(i, k, e.target.value)} className={`h-7 px-1.5 text-xs ${w}`} data-testid={`image-ligne-${i}-${k}`} />
                </td>
              ))}
              <td className="px-1">
                <button type="button" onClick={() => setLignes(lignes.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" title="Retirer la ligne" data-testid={`image-ligne-${i}-retirer`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Lit chaque image par l'IA, montre les lignes à vérifier, puis les importe
export const ImageImportDialog = ({ type, images, onFini }) => {
  const [item, setItem] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [notes, setNotes] = useState("");
  const [etat, setEtat] = useState("");
  useEffect(() => {
    const it = images[0];
    setItem(it || null);
    if (!it) { return undefined; }
    let annule = false;
    setEtat("lecture");
    const fd = new FormData();
    fd.append("type", type);
    fd.append("file", it.file);
    api.post("/imports/image/lire", fd)
      .then(({ data }) => { if (!annule) { setLignes(data.lignes); setNotes(data.notes); setEtat(""); } })
      .catch((e) => { if (!annule) { toast.error(`${it.file.name} : ${errMsg(e)}`); onFini(it, null); } });
    return () => { annule = true; };
  }, [images, type]); // eslint-disable-line react-hooks/exhaustive-deps
  const importer = async () => {
    setEtat("import");
    const fd = new FormData();
    fd.append("type", type);
    fd.append("logement", item.logement || "");
    fd.append("lignes", JSON.stringify(lignes.map(versEnvoi)));
    fd.append("file", item.file);
    try {
      const { data } = await api.post("/imports/image/importer", fd);
      onFini(item, data);
    } catch (e) {
      toast.error(errMsg(e));
      setEtat("");
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o && etat !== "import") { onFini(item, null); } }}>
      <DialogContent className="max-w-[min(96vw,90rem)]" data-testid="image-import-dialog">
        <DialogHeader>
          <DialogTitle>Vérifier les lignes lues sur l'image</DialogTitle>
          <DialogDescription>{item?.file.name} — corrigez ou retirez les lignes avant l'import. {notes}</DialogDescription>
        </DialogHeader>
        {etat === "lecture" ? (
          <p className="flex items-center gap-2 py-10 text-sm text-slate-500" data-testid="image-import-lecture"><Loader2 className="h-4 w-4 animate-spin" />Lecture de l'image en cours…</p>
        ) : (
          <>
            {lignes.length ? <Grille type={type} lignes={lignes} setLignes={setLignes} />
              : <p className="py-6 text-sm text-slate-500" data-testid="image-import-aucune">Aucune ligne reconnue : ajoutez-les à la main ou essayez une image plus nette.</p>}
            <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => setLignes([...lignes, vide(type)])} data-testid="image-import-ajouter">
              <Plus className="mr-1 h-3.5 w-3.5" />Ajouter une ligne
            </Button>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onFini(item, null)} disabled={etat === "import"} data-testid="image-import-ignorer">Ignorer cette image</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={importer} disabled={!!etat || !lignes.length} data-testid="image-import-confirmer">
            {etat === "import" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Importer {lignes.length} ligne(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
